import { ref, watch, computed } from '@vue/composition-api'
import store from '@/store'
import useNotifications from '@/composables/useNotifications'
import i18n from '@/libs/i18n'
import useCommon from '@/views/organization/useCommon'
import { hasPermission } from '@/composables/usePermission'
import { useLambda } from '@/views/habit/awsLambda'
import { oid } from '@/helpers/mongoHelpers'
import useMongoDBFunctions from '@/views/habit/useMongoDBFunctions'
import { EventBus } from '@/views/habit/EventBus.js'

export default function useTreatmentList() {
  const { showSuccessMessage, showErrorMessage } = useNotifications()
  const { handleError } = useCommon()
  const { updateItem, createItem, getItemsAggregate, updateItems } = useLambda()
  const { getLastCode } = useMongoDBFunctions()
  const { client, worker_id, role } = store.state?.userStore?.userData

  const refTreatmentTable = ref(null)
  const treatments = ref([])
  const perPage = ref(10)
  const totalTreatments = ref(0)
  const currentPage = ref(1)
  const searchQuery = ref('')
  const sortBy = ref('treatmentId')
  const isSortDirDesc = ref(true)
  const isLoading = ref(true)
  const collection = 'treatment'

  // Filters (from Filters component)
  const locationFilter = ref([])
  const rolFilter = ref(null)
  const volumenFilter = ref(null)
  const ambitoFilter = ref(null)
  const sensiblesFilter = ref(null)
  const transferenciaFilter = ref(null)

  const clearFiltersDisabled = computed(() =>
    !searchQuery.value &&
    !locationFilter.value?.length &&
    !rolFilter.value &&
    !volumenFilter.value &&
    !ambitoFilter.value &&
    sensiblesFilter.value === null &&
    transferenciaFilter.value === null
  )

  // Update filters from Filters component
  const updateFilter = (data) => {
    locationFilter.value = data.locationFilter || []
    rolFilter.value = data.rolPrivacyFilter
    volumenFilter.value = data.volumenFilter
    ambitoFilter.value = data.ambitoFilter
    sensiblesFilter.value = data.sensiblesFilter
    transferenciaFilter.value = data.transferenciaFilter

    checkPageAndFetch()
  }

  // Table Handlers
  const tableColumns = computed(() => {
    return [
      {
        key: 'actions',
        label: i18n.t('message.tableHeader.actions'),
      },
      {
        key: 'treatmentId',
        label: 'ID',
        sortable: true,
      },
      {
        key: 'nombre',
        label: 'Nombre',
        sortable: true,
      },
      {
        key: 'rol',
        label: 'Rol',
        sortable: true,
      },
      {
        key: 'departamento',
        label: 'Departamento',
        sortable: true,
      },
      {
        key: 'hasSensitive',
        label: 'Sensibles',
        sortable: true,
      },
      {
        key: 'hasMinors',
        label: 'NNA',
        sortable: true,
      },
      {
        key: 'hasBiometric',
        label: 'Biométricos',
        sortable: true,
      },
    ]
  })

  const fetchTreatments = async () => {
    isLoading.value = true

    try {
      // Build query
      const query = {
        client_id: oid(client.$oid),
        deleted: { $ne: true }
      }

      // Apply filters
      if (locationFilter.value?.length) {
        query.departamento_id = { $in: locationFilter.value.map(id => oid(id)) }
      }
      if (rolFilter.value) query.rol = rolFilter.value
      if (volumenFilter.value) query.volumen = volumenFilter.value
      if (ambitoFilter.value) query.ambito = ambitoFilter.value
      if (sensiblesFilter.value !== null) query.hasSensitive = sensiblesFilter.value
      if (transferenciaFilter.value !== null) {
        query['transferencias.destinatario'] = transferenciaFilter.value ? { $exists: true, $ne: '' } : { $in: [null, ''] }
      }

      // Search in nombre and finalidades
      if (searchQuery.value) {
        query.$or = [
          { nombre: { $regex: searchQuery.value, $options: 'i' } },
          { finalidades: { $regex: searchQuery.value, $options: 'i' } }
        ]
      }

      // Clean null values
      Object.keys(query).forEach(key => {
        if (query[key] === null) delete query[key]
      })

      const pipeline = [
        { $match: query },
        {
          $lookup: {
            from: 'inherent_analysis',
            localField: '_id',
            foreignField: 'treatment_id',
            as: 'ariRelated'
          }
        },
        {
          $lookup: {
            from: 'residual_analysis',
            localField: '_id',
            foreignField: 'treatment_id',
            as: 'arrRelated'
          }
        },
        {
          $lookup: {
            from: "location",
            localField: "departamento_id",
            foreignField: "_id",
            as: "departamento",
            pipeline: [ { $project: { location: 1 } } ]
          }
        },
        {
          $addFields: {
            relatedCount: { $add: [{ $size: '$ariRelated' }, { $size: '$arrRelated' }] },
            departamento: { $arrayElemAt: ["$departamento.location", 0] }
          }
        },
        {
          $project: {
            ariRelated: 0,
            arrRelated: 0
          }
        },
        { $sort: { [sortBy.value]: isSortDirDesc.value ? -1 : 1 } },
        { $skip: (currentPage.value - 1) * perPage.value },
        { $limit: perPage.value }
      ]

      const countPipeline = [
        { $match: query },
        { $count: 'total' }
      ]

      const result = await getItemsAggregate({ collection, pipeline })

      const countResult = await getItemsAggregate({ collection, pipeline: countPipeline })

      result?.forEach(treatment => {
        treatment.rolComputed = computed(() => treatment.rol ? i18n.t(`label.${treatment.rol}`) : '')
      })

      treatments.value = result || []
      totalTreatments.value = countResult?.[0]?.total || 0
    } catch (error) {
      console.log(error)
      handleError({ error, defaultMessage: 'Error al cargar tratamientos' })
    } finally {
      isLoading.value = false
    }
  }

  const deleteTreatment = async (treatment) => {
    if (!hasPermission('privacy-treatment', 'delete')) {
      showErrorMessage(i18n.t('message.you_are_not_authorized'))
      return
    }
    try {
      const query = { _id: oid(treatment._id), client_id: oid(client.$oid) }

      // Delete in cascade: treatment, inherentAnalysis, residualAnalysis, gap docs
      // First delete related documents
      // const relatedDocQuery = { treatment_id: oid(treatment._id) }
      // await updateItems({ collection: 'inherent_analysis', query: relatedDocQuery, action: { $set: { deleted: true } } })
      // await updateItems({ collection: 'residual_analysis', query: relatedDocQuery, action: { $set: { deleted: true } } })
      // await updateItems({ collection: 'gap', query: relatedDocQuery, action: { $set: { deleted: true } } })

      // Finally delete treatment
      const payload = { deleted: true }
      const action = { $set: payload }
      await updateItem({ collection, query, action })

      showSuccessMessage('Tratamiento eliminado correctamente')
      fetchTreatments()
    } catch (error) {
      console.log(error)
      showErrorMessage('Error al eliminar tratamiento')
    }
  }

  const duplicateTreatment = async (treatment) => {
    if (!hasPermission('privacy-treatment', 'create')) {
      showErrorMessage(i18n.t('message.you_are_not_authorized'))
      return
    }
    try {
      isLoading.value = true

      // Copy all fields except _id
      const { _id, ...treatmentData } = treatment

      // Generate treatmentId using getLastCode (T-001, T-002, etc.)
      const counterQuery = {
        collection,
        client_id: client.$oid
      }
      const lastCode = await getLastCode(counterQuery)
      const newCode = lastCode + 1
      const newTreatmentId = `T-${String(newCode).padStart(3, '0')}`

      const payload = {
        client_id: oid(client.$oid),
        treatmentId: newTreatmentId,
        nombre: `(Copia) ${treatmentData.nombre}`,
        empresa_id: treatmentData.empresa_id ? oid(treatmentData.empresa_id) : null,
        rol: treatmentData.rol,
        departamento_id: treatmentData.departamento_id ? oid(treatmentData.departamento_id) : null,
        responsable_id: treatmentData.responsable_id ? oid(treatmentData.responsable_id) : null,
        finalidades: treatmentData.finalidades,
        legitimacion: treatmentData.legitimacion,
        mecanismoConsentimiento: treatmentData.mecanismoConsentimiento || '',
        tiposDatos: treatmentData.tiposDatos || [],
        hasSensitive: treatmentData.hasSensitive,
        hasMinors: treatmentData.hasMinors,
        hasBiometric: treatmentData.hasBiometric,
        excepcionSensible: treatmentData.excepcionSensible || '',
        sensiblesDescripcion: treatmentData.sensiblesDescripcion || '',
        periodicidad: treatmentData.periodicidad,
        origen: treatmentData.origen,
        categoriaInteresados: treatmentData.categoriaInteresados || '',
        medios: treatmentData.medios || '',
        volumen: treatmentData.volumen,
        soporte: treatmentData.soporte || '',
        formato: treatmentData.formato || '',
        ambito: treatmentData.ambito,
        plazoConservacion: treatmentData.plazoConservacion,
        terceros: {
          fuenteCedente: treatmentData.terceros?.fuenteCedente || '',
          fuenteRegularizacion: treatmentData.terceros?.fuenteRegularizacion || '',
          destinatario: treatmentData.terceros?.destinatario || '',
          destinatarioFinalidad: treatmentData.terceros?.destinatarioFinalidad || '',
          destinatarioLegitimacion: treatmentData.terceros?.destinatarioLegitimacion || '',
        },
        encargados: {
          identificacion: treatmentData.encargados?.identificacion || '',
          prestacion: treatmentData.encargados?.prestacion || '',
        },
        transferencias: {
          destinatario: treatmentData.transferencias?.destinatario || '',
          pais: treatmentData.transferencias?.pais || '',
        },
        resguardos: {
          papel: treatmentData.resguardos?.papel || '',
          sistemas: treatmentData.resguardos?.sistemas || '',
        },
        observaciones: treatmentData.observaciones || '',
        status: 'draft',
      }

      await createItem({ collection, payload })

      showSuccessMessage('Tratamiento duplicado correctamente')
      fetchTreatments()
    } catch (error) {
      console.log(error)
      showErrorMessage('Error al duplicar tratamiento')
    } finally {
      isLoading.value = false
    }
  }

  const clearFilters = () => {
    searchQuery.value = ''
    locationFilter.value = []
    rolFilter.value = null
    volumenFilter.value = null
    ambitoFilter.value = null
    sensiblesFilter.value = null
    transferenciaFilter.value = null

    // Emit event to reset filters in Filters component
    EventBus.$emit('resetFilters')

    checkPageAndFetch()
  }

  const checkPageAndFetch = () => {
    if (currentPage.value === 1) fetchTreatments()
    else currentPage.value = 1
  }

  // Fetch all treatments for export (respecting filters, no pagination)
  const fetchAllTreatmentsForExport = async () => {
    try {
      // Build query same as fetchTreatments
      const query = {
        client_id: oid(client.$oid),
        deleted: { $ne: true }
      }

      // Apply filters
      if (locationFilter.value?.length) {
        query.departamento_id = { $in: locationFilter.value.map(id => oid(id)) }
      }
      if (rolFilter.value) query.rol = rolFilter.value
      if (volumenFilter.value) query.volumen = volumenFilter.value
      if (ambitoFilter.value) query.ambito = ambitoFilter.value
      if (sensiblesFilter.value !== null) query.hasSensitive = sensiblesFilter.value
      if (transferenciaFilter.value !== null) {
        query['transferencias.destinatario'] = transferenciaFilter.value ? { $exists: true, $ne: '' } : { $in: [null, ''] }
      }

      // Search in nombre and finalidades
      if (searchQuery.value) {
        query.$or = [
          { nombre: { $regex: searchQuery.value, $options: 'i' } },
          { finalidades: { $regex: searchQuery.value, $options: 'i' } }
        ]
      }

      // Clean null values
      Object.keys(query).forEach(key => {
        if (query[key] === null) delete query[key]
      })

      const pipeline = [
        { $match: query },
        {
          $lookup: {
            from: "location",
            localField: "departamento_id",
            foreignField: "_id",
            as: "departamento",
            pipeline: [ { $project: { location: 1 } } ]
          }
        },
        {
          $lookup: {
            from: "company",
            localField: "empresa_id",
            foreignField: "_id",
            as: "empresa",
            pipeline: [ { $project: { name: 1 } } ]
          }
        },
        {
          $addFields: {
            departamento: { $arrayElemAt: ["$departamento.location", 0] },
            empresa: { $arrayElemAt: ["$empresa", 0] }
          }
        },
        { $sort: { treatmentId: -1 } }
      ]

      const result = await getItemsAggregate({ collection, pipeline })

      return result || []
    } catch (error) {
      console.log(error)
      handleError({ error, defaultMessage: 'Error al cargar tratamientos para exportar' })
      return []
    }
  }

  watch([currentPage], () => {
    fetchTreatments()
  })

  watch([searchQuery, sortBy, isSortDirDesc, perPage], () => {
    checkPageAndFetch()
  })

  return {
    fetchTreatments,
    tableColumns,
    perPage,
    currentPage,
    totalTreatments,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refTreatmentTable,
    treatments,
    deleteTreatment,
    duplicateTreatment,
    isLoading,
    clearFilters,
    clearFiltersDisabled,
    updateFilter,
    fetchAllTreatmentsForExport,
  }
}
